import React, { useEffect, useState } from 'react';
import { Image } from "react-native"
import type { CompanyProps, CompetitionProps, FocusPositionProps, GroupProps, MyPlayerProps } from '../../types';
import { Icons, Switch } from '../../Components';
import ImageUploader from '../../Components/ImageUploader';
import { Button, Text, TextInput, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';
import GroupSelector from './GroupSelector';
import CompanySelector from './CompanySelector';

type CompetitionInfoFormProps = {
    competition:CompetitionProps,
    player?:MyPlayerProps,
    groups:GroupProps[],
    companies:CompanyProps[],
    onFocusPosition?:(position:FocusPositionProps) => void,
    init_expanded?:boolean,
    onCompetitionUpdate:(competition:CompetitionProps) => void
}

const CompetitionInfoForm = ({ onFocusPosition, init_expanded, player, competition, groups, companies, onCompetitionUpdate }:CompetitionInfoFormProps) => {
    const [ expanded, setExpanded ] = useState(init_expanded ?? false);
    const [ draft_competition, setDraftCompetition ] = useState<CompetitionProps | undefined>(undefined);
    const Colors = useColors();
    useEffect(() => {
        setDraftCompetition(competition)
    },[competition])

    if(!draft_competition){ return <></> }
    const active_group = groups.find(g => g.group_id == competition.group_id);
    const active_company = companies.find(c => c.company_id == competition.company_id);

    const is_changed = JSON.stringify(draft_competition) != JSON.stringify(competition) ? true : false;
    return (
        <View style={{ borderRadius:0 }}>
            <Button 
                disabled={init_expanded}
                float 
                style={{ flexDirection:'row', alignItems:'center', borderRadius:0, padding:20, backgroundColor:Colors.toggle.button.active}}
                onPress={() => setExpanded(!expanded)}>
                    <View transparent style={{ flex:1 }}>
                        <Text theme='h1' color={Colors.toggle.text.active}>Competition Information</Text>
                        <Text style={{ marginTop:3 }} theme='description' color={Colors.toggle.text.active}>Manage the high level information of this competition</Text>
                    </View>
                {is_changed ?
                <Button
                    title='SAVE'
                    type='success'
                    onPress={() => onCompetitionUpdate(draft_competition)}
                />
                : !init_expanded ?
                <Icons.ChevronIcon direction={expanded ? 'up' : 'down'} color={Colors.text.h1} size={8} />
                :<></>
                }
            </Button>
            {expanded ?
            <View type='body' nativeID="competition_info">
                
                <View type='row' nativeID="competition_image" style={{ alignItems:'flex-start', borderBottomWidth:1, borderColor:Colors.borders.light, padding:20 }}>
                    <View style={{ flex:1 }}>
                        <Text theme='h1'>Competition Image</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Give this competition an image so it is recognizable to those trying to join.</Text>
                    </View>
                    <ImageUploader
                        public_id={`comp_image_${competition.competition_id}_${Math.random()}`}
                        onFinishUpload={obj => onCompetitionUpdate({ ...draft_competition, image: { ...obj, url:obj.secure_url } })}
                    >
                        <Image
                            source={{ uri: competition.image?.url }}
                            style={{ height: 50, width:50, borderRadius:4 }}
                            resizeMode='cover'
                        />
                        <Text style={{ marginTop:10 }} size={12} color={Colors.text.action} textAlign='center'>CHANGE</Text>
                    </ImageUploader>
                </View>

                <View type='row' nativeID="competition_name" style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ flex:1, minWidth:200 }}>
                        <Text theme='h1'>Competition Name</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Give the competition a memorable name that aligns with the type and payout.</Text>
                    </View>
                    <TextInput
                        style={{ flex:1, minWidth:200, margin:5 }}
                        onFocusPosition={onFocusPosition}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, competition_name:text })}
                        value={draft_competition.competition_name}
                        placeholder="Competition Name"
                    />
                </View>
                <View type='row' nativeID="competition_description" style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ flex:1, minWidth:200  }}>
                        <Text theme='h1'>Competition Description</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Give the competition a memorable description that would entice users to participate.</Text>
                    </View>
                    <TextInput
                        style={{ flex:1, minWidth:200, margin:5 }}
                        onChangeText={(text) => setDraftCompetition({ ...draft_competition, competition_description:text })}
                        value={draft_competition.competition_description}
                        onFocusPosition={onFocusPosition}
                        placeholder="Pick 5 games ATS from NFL Week 6 matchups"
                    />
                </View>
                <View type='row' nativeID="invite_only" style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light  }}>
                    <View style={{ flex:1, marginRight:15 }}>
                        <Text theme='h1'>Make Private</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Only access this competition using an invite code.</Text>
                    </View>
                    <Switch
                        disabled={competition.competition_season_id ? true : false}
                        value={competition.invite_only}
                        switch_type="on_off"
                        onChange={(value) => {
                            if(player?.role != 'admin'){ return alert('You do not have access to make public competitions at this time.') }
                            onCompetitionUpdate({ ...draft_competition, invite_only: value })
                        }}
                    />
                </View>
                {player?.role == 'admin' ?
                <View type='row' nativeID="invite_only" style={{ flexWrap:'wrap', padding:20, borderBottomWidth:1, borderColor:Colors.borders.light  }}>
                    <View style={{ flex:1, marginRight:15 }}>
                        <Text theme='h1'>Make Template</Text>
                        <Text style={{ marginTop:3 }} theme='description'>This will allow for other users to copy this competition and create it for themselves.</Text>
                    </View>
                    <Switch
                        value={competition.template ? true : false}
                        switch_type="on_off"
                        onChange={(value) => onCompetitionUpdate({ ...draft_competition, template: value })}
                    />
                </View>
                :<></>}
                {groups.length > 0 ?
                <View style={{ padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ marginBottom:10 }}>
                        <Text theme='h1'>My Groups</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Add this competition to a group that you are a member of</Text>
                    </View>
                    <GroupSelector
                        active_group={active_group}
                        groups={groups}
                        onSelectGroup={(g) => onCompetitionUpdate({ ...draft_competition, group_id: g?.group_id })}
                    />
                </View>
                :<></>}
                {companies.length > 0 ?
                <View style={{ padding:20, borderBottomWidth:1, borderColor:Colors.borders.light }}>
                    <View style={{ marginBottom:10 }}>
                        <Text theme='h1'>My Companies</Text>
                        <Text style={{ marginTop:3 }} theme='description'>Add this competition to a company that you are a member of</Text>
                    </View>
                    <CompanySelector
                        active_company={active_company}
                        companies={companies}
                        onSelectCompany={(c) => onCompetitionUpdate({ ...draft_competition, company_id: c?.company_id })}
                    />
                </View>
                :<></>}
            </View>
            :<></>}
        </View>
    )
}

export default CompetitionInfoForm

